'use client';
import React from 'react';
import { AlertTriangle, CheckCircle, HelpCircle, Clock, ArrowDown } from 'lucide-react';
import { useCertificates } from '@/contexts/CertificatesContext';
import { computeCertificateHealth, computeReadinessV1, type ReadinessStatus } from '@/lib/certificate-alerts';

type Config = {
  label: string;
  Icon: typeof AlertTriangle;
  fgColor: string;
  bgColor: string;
  borderColor: string;
  neutralStyle: boolean;
};

const STATUS_CONFIG: Record<ReadinessStatus, Config> = {
  expired: {
    label: 'Expired Certificate Detected',
    Icon: AlertTriangle,
    fgColor: 'var(--status-critical)',
    bgColor: 'var(--status-critical-bg)',
    borderColor: 'var(--status-critical)',
    neutralStyle: false,
  },
  renewal: {
    label: 'Renewal Required',
    Icon: Clock,
    fgColor: 'var(--status-expiring-soon)',
    bgColor: 'var(--status-expiring-soon-bg)',
    borderColor: 'var(--status-expiring-soon)',
    neutralStyle: false,
  },
  review: {
    label: 'Review Requirement',
    Icon: HelpCircle,
    fgColor: '',
    bgColor: '',
    borderColor: '',
    neutralStyle: true,
  },
  ok: {
    label: 'No Expiry Attention Needed',
    Icon: CheckCircle,
    fgColor: 'var(--status-valid)',
    bgColor: 'var(--status-valid-bg)',
    borderColor: 'var(--status-valid)',
    neutralStyle: false,
  },
};

function formatExpiryLabel(name: string, days: number): string {
  if (days < 0) return `${name} — expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
  if (days === 0) return `${name} — expires today`;
  return `${name} — ${days} day${days !== 1 ? 's' : ''} remaining`;
}

export default function CertificateReadinessCard() {
  const { certificates } = useCertificates();
  const readiness = computeReadinessV1(certificates);
  const health = computeCertificateHealth(certificates);

  if (!readiness) return null;

  const cfg = STATUS_CONFIG[readiness.status];
  const { Icon } = cfg;

  const containerStyle = cfg.neutralStyle
    ? undefined
    : { backgroundColor: cfg.bgColor, borderColor: cfg.borderColor };
  const containerClass = `readiness-card rounded-xl border px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3${cfg.neutralStyle ? ' card-elevated' : ''}`;
  const labelStyle = cfg.neutralStyle ? undefined : { color: cfg.fgColor };
  const iconStyle = cfg.neutralStyle ? undefined : { color: cfg.fgColor };
  const statusLabel =
    readiness.status === 'expired' && health.expired !== 1
      ? 'Expired Certificates Detected'
      : cfg.label;
  const scrollToCertificates = () => {
    document.getElementById('certificate-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={containerClass} style={containerStyle}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Icon
          size={20}
          className={`shrink-0 mt-0.5${cfg.neutralStyle ? ' text-primary' : ''}`}
          style={iconStyle}
        />
        <div className="min-w-0">
          <p className="text-xs font-600 text-muted-foreground mb-0.5">
            Certificate Status
          </p>
          <p
            className={`text-sm font-700 leading-snug${cfg.neutralStyle ? ' text-foreground' : ''}`}
            style={labelStyle}
          >
            {statusLabel}
          </p>
          {readiness.status === 'review' ? (
            readiness.reviewCount > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {readiness.reviewCount} certificate{readiness.reviewCount !== 1 ? 's' : ''} require requirement review
              </p>
            )
          ) : (
            readiness.status === 'expired' ? (
              <p className="text-xs text-muted-foreground mt-0.5">
                {health.expired} expired certificate{health.expired !== 1 ? 's' : ''} detected
              </p>
            ) : readiness.attentionCount > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {readiness.attentionCount} certificate{readiness.attentionCount !== 1 ? 's' : ''} requiring attention
              </p>
            )
          )}
          {readiness.nextExpiry !== null && (
            <div className="mt-2">
              <p className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">
                {readiness.status === 'expired' ? 'Most Critical' : 'Nearest Expiry'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 break-words">
                {formatExpiryLabel(readiness.nextExpiry.name, readiness.nextExpiry.daysRemaining)}
              </p>
            </div>
          )}
        </div>
      </div>
      {readiness.status !== 'ok' && (
        <button
          type="button"
          onClick={scrollToCertificates}
          className="readiness-action inline-flex items-center justify-center gap-1.5 self-start sm:self-center rounded-lg border border-current px-3 py-2 text-xs font-700 transition-opacity hover:opacity-75"
          style={labelStyle}
        >
          <ArrowDown size={13} />
          View Certificates
        </button>
      )}
    </div>
  );
}
